import { useState, useEffect, useMemo } from 'react';
import { useNavigate, NavLink as RouterLink } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Divider,
  Grid,
  Link,
  Typography,
  Tabs,
  Tab
} from '@material-ui/core';
import { styled } from '@mui/material';

import { getComparedData, getNameByVal } from 'helpers/listValues';
import { RootState } from 'redux/store';
import { getCardValuesByType } from 'helpers/cardValues';
import { HOST_URL, PATH_AUTH, PATH_HOME, PATH_DASHBOARD } from 'routes/paths';
import useLocales from 'hooks/useLocales';
import { getTexts } from 'constants/text/texts';
import useAuth from 'hooks/useAuth';
import { SALARY_CHOICES_INR } from 'constants/constants';
import { fDateTimeBy } from 'utils/formatTime';
import {
  createSortedPoints,
  getCompetencyScoresAndLabels
} from 'services/competencyService';
import { fetchMindgageMetrics } from 'redux/slices/assessment';

import { SharedButtons } from './SharedButtons';
import { ApplyJobButton } from './ApplyJobButton';
import Logo from './Logo';
import CultureProfileCharts, { ChartDescription } from './CultureProfileCharts';
import { DeleteButton, EditButton } from './Buttons';
import { TestAssessmentButton } from './TestAssessmentButton';
import { Description5, Title5, Title6, TitlePrimary1 } from './text/title';
import { ButtonPrimary } from './new-designer/button/ButtonPrimary';
import CandidateModal from './CandidateModal';
import ScoreField from './ScoreField';
import CognitiveMatchScore from './CognitiveMatchScore';

export interface OpenJobPostType {
  created_at?: string;
  slug: string;
  id: number;
  title: string;
  studies: [{ id: number; name: string }];
  degree: number;
  work_exp: number;
  locations: any[];
  industries: any[];
  skills: any[];
  musts: any[];
  perks: any[];
  status: string;
  salary: number;
  contract: number;
  available: string;
  drivers_license: boolean;
  description: string;
  user: number;
  culture_profile: any;
  competency_labels: string[];
  competency_min_scores: number[];
  competencies: any[];
  view_count: number;
  is_applied?: boolean;
  assessment_required?: boolean;
  customer: any;
  user_name: string;
  culture_score: string;
  score: string;
  mindgage_metrics?: Array<{ metric_id: number; min_score: number }>;
}

export interface OpenJobPostProps {
  item: OpenJobPostType;
  onDelete: any;
  onEdit: any;
  editable?: boolean;
  compared?: any;
  nocustomer?: boolean;
  fullWidth?: boolean;
  showCompetencyComparison?: boolean;
  candidateCompetencies?: any[];
  showJobScore?: boolean;
  showCultureScore?: boolean;
}

const TitleTypography = styled(Typography)(() => ({
  fontFamily: 'Rubik',
  fontWeight: 600,
  fontSize: '13px',
  color: '#131417',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  lineHeight: '25px'
}));

const BoxStyle = styled(Box)(() => ({
  '@media (max-width: 768px)': {
    flexDirection: 'column',
    rowGap: '10px',
    '&& .MuiTypography-body1': {
      fontSize: '24px !important'
    },
    img: {
      width: '40px',
      height: '40px'
    }
  }
}));

const GridStyle = styled(Grid)(() => ({
  '@media (max-width: 768px)': {
    '.title': {
      fontSize: '20px'
    }
  }
}));

const CardFooterContainer = styled('div')(
  ({ canTakeAction }: { canTakeAction: boolean }) => ({
    padding: '15px 0 0 0',
    display: 'flex',
    justifyContent: canTakeAction ? 'space-between' : 'end',
    alignItems: 'center',
    borderTop: '1px solid #e0e0e0',
    width: '100%'
  })
);

const CardStyles = styled(Card)(
  ({
    theme,
    fromCandidate,
    fullWidth
  }: {
    theme?: any;
    fromCandidate: boolean;
    fullWidth?: boolean;
  }) => ({
    '&:hover': {
      ...(fromCandidate && {
        boxShadow:
          '0 0 100px 0 rgb(145 158 171 / 24%), 0 0 100px 0 rgb(145 158 171 / 24%)'
      })
    },
    maxWidth: fullWidth ? '100%' : '469px',
    width: fullWidth ? '100%' : 'auto',
    maxHeight: fullWidth ? 'auto' : '730px',
    [theme.breakpoints.up('lg')]: {
      maxWidth: fullWidth ? '100%' : '469px',
      width: fullWidth ? '100%' : 'auto',
      maxHeight: fullWidth ? 'auto' : '730px'
    }
  })
);

const HrDivider = styled(Divider)(() => ({
  height: 0,
  border: '0.6px solid #C9CAD9'
}));

export const OpenJobPostCard = ({
  item,
  onDelete,
  onEdit,
  editable = true,
  compared = undefined,
  nocustomer = false,
  fullWidth = false,
  showCompetencyComparison = true,
  candidateCompetencies: passedCandidateCompetencies,
  showJobScore = true,
  showCultureScore = true
}: OpenJobPostProps): JSX.Element => {
  const [showModal, setShowModal] = useState(false);
  const [scoresTooltipIsOpen, setScoresTooltipIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [mindgageScoresTooltipIsOpen, setMindgageScoresTooltipIsOpen] =
    useState(false);
  const { mainInfo } = useSelector(
    (state: RootState) => state.candidate.candidate
  );
  // Use passed candidateCompetencies if available, otherwise fallback to mainInfo.competencies
  // Ensure candidateCompetencies is always an array
  const candidateCompetencies = (() => {
    if (Array.isArray(passedCandidateCompetencies)) {
      return passedCandidateCompetencies;
    }
    return Array.isArray(mainInfo?.competencies) ? mainInfo?.competencies : [];
  })();
  const cultureCompared = getComparedData(
    candidateCompetencies,
    item?.competencies
  );

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, currentLang } = useLocales();
  const { getViewed } = getTexts();
  const { result: competencies }: any = useSelector(
    (state: RootState) => state.candidate.assessmentResult
  );

  const { company }: any = useSelector(
    (state: RootState) => state.generalRecruiter
  );
  const { mindgageMetrics } = useSelector(
    (state: RootState) => state.assessment
  );

  const {
    user,
    userId,
    isAdmin,
    isRecruiterPlus,
    isCandidate,
    isEmployee,
    isRecruiter,
    isHiringManager
  } = useAuth();
  const isCandidateOrEmployee = isCandidate || isEmployee;

  const isOwner = item.user === userId;

  const ownedByBoundingRecruiter = item.user === user.recruiter;

  const canTakeAction =
    isAdmin ||
    isRecruiterPlus ||
    (isRecruiter && isOwner) ||
    (isHiringManager && ownedByBoundingRecruiter);

  const onShowModal = () => {
    setShowModal(true);
  };

  const onCloseModal = () => {
    setShowModal(false);
  };

  // Determine which competency data source to use based on user role
  // For candidates/employees: Use their assessment result competencies directly
  // For admins/recruiters: Use the passed candidate competencies
  const competenciesForComparison = isCandidateOrEmployee
    ? competencies
    : candidateCompetencies;

  // Use competency service for consistent handling
  const sortedPoints = item.competency_labels?.length
    ? createSortedPoints(
        item.competency_labels,
        item.competency_min_scores,
        competenciesForComparison || [],
        item.competencies?.map((el: any) => ({
          id: el.competency.id,
          name: el.competency.name,
          description: el.competency.description
        })) || []
      )
    : [];

  // Check if competencies and mindgage_metrics exist
  const hasCompetencies = item.competencies && item.competencies.length > 0;
  const hasMindgageMetrics =
    item.mindgage_metrics && item.mindgage_metrics.length > 0;

  // Set initial activeTab based on what's available
  useEffect(() => {
    if (hasCompetencies && hasMindgageMetrics) {
      setActiveTab(0);
    } else if (hasCompetencies) {
      setActiveTab(0);
    } else if (hasMindgageMetrics) {
      setActiveTab(0);
    }
  }, [hasCompetencies, hasMindgageMetrics]);

  // Fetch mindgage metrics if needed
  useEffect(() => {
    if (
      hasMindgageMetrics &&
      (!mindgageMetrics || mindgageMetrics.length === 0)
    ) {
      dispatch(fetchMindgageMetrics(currentLang?.value || 'en') as any);
    }
  }, [hasMindgageMetrics, mindgageMetrics, dispatch, currentLang]);

  // Get candidate mindgage result for comparison
  const candidateMindgageResult = useMemo(() => {
    const mainInfoAny = mainInfo as any;
    // Check multiple possible paths for mindgage result
    // Structure can be: mainInfo.mindgage.result or mainInfo.mainInfo.mindgage.result
    const mindgageFromMainInfo =
      mainInfoAny?.mindgage?.result || mainInfoAny?.mainInfo?.mindgage?.result;

    // The structure is an array of {metric_id, score} objects
    if (
      mindgageFromMainInfo &&
      Array.isArray(mindgageFromMainInfo) &&
      mindgageFromMainInfo.length > 0
    ) {
      return mindgageFromMainInfo;
    }

    return null;
  }, [mainInfo]);

  // Calculate cognitive match percentage based on job requirements and candidate results
  const calculatedMindgageScore = useMemo(() => {
    if (
      !item.mindgage_metrics ||
      item.mindgage_metrics.length === 0 ||
      !candidateMindgageResult ||
      candidateMindgageResult.length === 0
    ) {
      return null;
    }

    // Create a map of metric_id -> score from candidate's mindgage result
    const candidateScoreMap = new Map<number, number>();
    candidateMindgageResult.forEach((m: any) => {
      if (m.metric_id && m.score !== undefined) {
        candidateScoreMap.set(m.metric_id, m.score);
      }
    });

    // Count how many metrics the candidate meets or exceeds the minimum requirement
    let matchedMetrics = 0;
    let totalMetrics = 0;

    item.mindgage_metrics.forEach((jobMetric: any) => {
      if (jobMetric.metric_id && jobMetric.min_score !== undefined) {
        totalMetrics++;
        const candidateScore = candidateScoreMap.get(jobMetric.metric_id);
        if (
          candidateScore !== undefined &&
          candidateScore >= jobMetric.min_score
        ) {
          matchedMetrics++;
        }
      }
    });

    // Calculate percentage: (matched / total) * 100
    if (totalMetrics === 0) return null;
    return Math.round((matchedMetrics / totalMetrics) * 100);
  }, [item.mindgage_metrics, candidateMindgageResult]);

  // Helper function to get cognitive match score
  const getCognitiveMatchScore = useMemo(() => {
    if (!isCandidateOrEmployee) {
      return null;
    }

    const hasCandidateResult =
      candidateMindgageResult &&
      Array.isArray(candidateMindgageResult) &&
      candidateMindgageResult.length > 0;
    const hasJobMetrics =
      item.mindgage_metrics &&
      Array.isArray(item.mindgage_metrics) &&
      item.mindgage_metrics.length > 0;

    // If we have both job metrics and candidate results, use calculated score
    if (hasJobMetrics && hasCandidateResult) {
      const displayScore = calculatedMindgageScore ?? 0;
      if (
        displayScore !== null &&
        displayScore !== undefined &&
        displayScore >= 0
      ) {
        return displayScore;
      }
    }

    // Fallback: show raw score if available (even if we can't calculate match)
    const fallbackScore = (item as any)?.mindgage_score;
    if (
      fallbackScore !== null &&
      fallbackScore !== undefined &&
      fallbackScore > 0
    ) {
      return fallbackScore;
    }

    return null;
  }, [
    isCandidateOrEmployee,
    candidateMindgageResult,
    item.mindgage_metrics,
    calculatedMindgageScore,
    item
  ]);

  // Process mindgage metrics data for chart
  const mindgageChartData = useMemo(() => {
    if (
      !hasMindgageMetrics ||
      !mindgageMetrics ||
      mindgageMetrics.length === 0
    ) {
      return null;
    }

    // Create a map of metric_id -> min_score from job's mindgage_metrics
    const scoreMap = new Map<number, number>();
    item.mindgage_metrics?.forEach((m: any) => {
      if (m.metric_id && m.min_score !== undefined) {
        scoreMap.set(m.metric_id, m.min_score);
      }
    });

    // Create a map of metric_id -> score from candidate's mindgage result
    const candidateScoreMap = new Map<number, number>();
    if (candidateMindgageResult && Array.isArray(candidateMindgageResult)) {
      candidateMindgageResult.forEach((m: any) => {
        if (m.metric_id && m.score !== undefined) {
          candidateScoreMap.set(m.metric_id, m.score);
        }
      });
    }

    // Map metrics to chart format
    const labels: string[] = [];
    const points: number[] = [];
    const comparePoints: (number | null)[] = [];
    const descriptions: any[] = [];

    mindgageMetrics.forEach((metric: any) => {
      if (metric.id && metric.name) {
        const score = scoreMap.get(metric.id);
        if (score !== undefined) {
          labels.push(metric.name);
          points.push(score);
          // Get candidate score for this metric if available
          const candidateScore = candidateScoreMap.get(metric.id);
          comparePoints.push(
            candidateScore !== undefined ? candidateScore : null
          );
          descriptions.push({
            id: metric.id,
            name: metric.name,
            description: metric.description || ''
          });
        }
      }
    });

    return labels.length > 0
      ? { labels, points, comparePoints, descriptions }
      : null;
  }, [
    hasMindgageMetrics,
    mindgageMetrics,
    item.mindgage_metrics,
    candidateMindgageResult
  ]);

  // Create sorted points for mindgage metrics (similar format to competencies)
  const sortedMindgagePoints = useMemo(() => {
    if (!mindgageChartData) {
      return [];
    }

    // Format mindgage data to match the structure expected by ChartDescription
    return mindgageChartData.labels
      .map((label: string, index: number) => {
        const description = mindgageChartData.descriptions[index];
        return {
          point: mindgageChartData.points[index] || 0,
          label,
          id: description?.id || index + 1,
          descriptions: description?.description || '',
          comparePoint:
            mindgageChartData.comparePoints[index] !== null &&
            mindgageChartData.comparePoints[index] !== undefined
              ? mindgageChartData.comparePoints[index]
              : null
        };
      })
      .sort((a: any, b: any) => (b.point || 0) - (a.point || 0));
  }, [mindgageChartData]);

  // Generate random data for placeholder chart
  const generateRandomChartData = (): {
    labels: string[];
    points: number[];
    descriptions: Array<{ id: number; name: string; description: string }>;
  } => {
    const randomLabels = [
      'Communication',
      'Problem Solving',
      'Teamwork',
      'Leadership',
      'Adaptability',
      'Creativity'
    ];
    const randomPoints = randomLabels.map(
      () => Math.floor(Math.random() * 3) + 2
    ); // Random between 2-4
    const randomDescriptions = randomLabels.map((name, index) => ({
      id: index + 1,
      name,
      description: `Description for ${name}`
    }));
    return {
      labels: randomLabels,
      points: randomPoints,
      descriptions: randomDescriptions
    };
  };

  const placeholderChartData = generateRandomChartData();

  return (
    <>
      <CardStyles
        fromCandidate={!!mainInfo?.id}
        className="open-job-posts-card"
        sx={mainInfo?.id && { cursor: 'pointer' }}
        onClick={() => {
          mainInfo?.id && !scoresTooltipIsOpen && onShowModal();
        }}
        fullWidth={fullWidth}
      >
        <CardContent style={{ paddingBottom: '0px' }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Link
                  onClick={onEdit}
                  sx={{
                    cursor: 'pointer',
                    width: '65%',
                    display: 'flex',
                    columnGap: '10px'
                  }}
                >
                  <TitleTypography
                    variant="h4"
                    style={{ textOverflow: 'ellipsis' }}
                  >
                    {item.title}{' '}
                  </TitleTypography>
                  <TitleTypography
                    variant="h4"
                    style={{ minWidth: 'max-content' }}
                  >
                    {!nocustomer && item?.customer && company?.is_agency
                      ? `[${item?.customer.name}]`
                      : ''}
                  </TitleTypography>
                </Link>
                {!isCandidateOrEmployee && (
                  <Typography variant="caption" color="GrayText">
                    {getViewed(item.view_count)}
                  </Typography>
                )}
              </Box>
              <Grid
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginTop: '10px',
                  flexWrap: 'wrap',
                  gap: '8px'
                }}
              >
                {!isCandidateOrEmployee && item.user_name && (
                  <TitleTypography
                    style={{ textOverflow: 'ellipsis', marginBottom: '8px' }}
                  >
                    {t('Created by')} {item.user_name}
                  </TitleTypography>
                )}
                {!isCandidateOrEmployee && (
                  <Typography
                    color="GrayText"
                    variant="caption"
                    style={{ textOverflow: 'ellipsis' }}
                  >
                    {item?.created_at && fDateTimeBy(item?.created_at)}
                  </Typography>
                )}
              </Grid>
              <Box
                display="flex"
                flexDirection="row"
                alignItems="flex-start"
                justifyContent="space-between"
                style={{ marginTop: item.score ? '12px' : 0 }}
              >
                <Box display="flex" flexDirection="column">
                  {item.score && showJobScore ? (
                    <ScoreField
                      score={item.score}
                      type="talent"
                      fontSize={20}
                    />
                  ) : (
                    <Box sx={{ height: '27px' }} />
                  )}
                  {(() => {
                    if (item.culture_score && showCultureScore) {
                      return (
                        <ScoreField
                          score={item.culture_score}
                          type="culture"
                          fontSize={20}
                        />
                      );
                    }
                    if (
                      !item.score &&
                      !item.culture_score &&
                      getCognitiveMatchScore !== null
                    ) {
                      return (
                        <CognitiveMatchScore
                          score={getCognitiveMatchScore}
                          t={t}
                        />
                      );
                    }
                    return <Box sx={{ height: '27px' }} />;
                  })()}
                </Box>
                {isCandidateOrEmployee &&
                (item.score || item.culture_score) &&
                getCognitiveMatchScore !== null ? (
                  <CognitiveMatchScore
                    score={getCognitiveMatchScore}
                    marginLeft="12px"
                    alignSelf="flex-start"
                    t={t}
                  />
                ) : null}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <HrDivider />
              {(() => {
                // Both charts - show tabs only if hideTabs is false
                if (hasCompetencies || hasMindgageMetrics) {
                  return (
                    <Box sx={{ minHeight: '400px' }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center'
                        }}
                      >
                        <Tabs
                          onClick={(e) => e.stopPropagation()}
                          value={activeTab}
                          onChange={(e, newValue) => setActiveTab(newValue)}
                          indicatorColor="primary"
                          textColor="primary"
                        >
                          {hasCompetencies && (
                            <Tab label={t('Job Competencies')} />
                          )}
                          {hasMindgageMetrics && (
                            <Tab label={t('Cognitive Metrics')} />
                          )}
                        </Tabs>
                      </Box>
                      {activeTab === 0 && hasCompetencies && (
                        <CultureProfileCharts
                          title={t('Competencies Score')}
                          labels={item.competency_labels}
                          points={item.competency_min_scores}
                          comparePoints={compared || cultureCompared}
                          compareTitle="candidate"
                          descriptions={item.competencies.map((el: any) => ({
                            description: el.competency.description,
                            name: el.competency.name,
                            id: el.competency.id
                          }))}
                          candidateCompetencies={
                            competenciesForComparison || []
                          }
                          scoresTooltipIsOpen={scoresTooltipIsOpen}
                          setScoresTooltipIsOpen={setScoresTooltipIsOpen}
                          showCompetencyComparison={showCompetencyComparison}
                        />
                      )}
                      {((activeTab === 1 &&
                        hasCompetencies &&
                        hasMindgageMetrics) ||
                        (activeTab === 0 &&
                          !hasCompetencies &&
                          hasMindgageMetrics)) &&
                        mindgageChartData && (
                          <CultureProfileCharts
                            title={t('Cognitive Metrics')}
                            isMindgage={true}
                            labels={mindgageChartData.labels}
                            points={mindgageChartData.points}
                            comparePoints={
                              mindgageChartData.comparePoints.some(
                                (p: any) => p !== null && p !== undefined
                              )
                                ? mindgageChartData.comparePoints
                                : undefined
                            }
                            compareTitle="candidate"
                            descriptions={mindgageChartData.descriptions}
                            profile={true}
                            showTooltips={false}
                            minScale={1}
                            maxScale={5}
                            mindgageScoresTooltipIsOpen={
                              mindgageScoresTooltipIsOpen
                            }
                            setMindgageScoresTooltipIsOpen={
                              setMindgageScoresTooltipIsOpen
                            }
                            showCompetencyComparison={mindgageChartData.comparePoints.some(
                              (p: any) => p !== null && p !== undefined
                            )}
                          />
                        )}
                    </Box>
                  );
                }

                // Both charts but hideTabs is true - show only competencies
                if (hasCompetencies) {
                  return (
                    <Box sx={{ minHeight: '390px' }}>
                      <CultureProfileCharts
                        title={t('Competencies Score')}
                        labels={item.competency_labels}
                        points={item.competency_min_scores}
                        comparePoints={compared || cultureCompared}
                        compareTitle="candidate"
                        descriptions={item.competencies.map((el: any) => ({
                          description: el.competency.description,
                          name: el.competency.name,
                          id: el.competency.id
                        }))}
                        candidateCompetencies={competenciesForComparison || []}
                        scoresTooltipIsOpen={scoresTooltipIsOpen}
                        setScoresTooltipIsOpen={setScoresTooltipIsOpen}
                        showCompetencyComparison={showCompetencyComparison}
                      />
                    </Box>
                  );
                }

                // Only competencies
                if (hasCompetencies) {
                  return (
                    <CultureProfileCharts
                      title={t('Competencies Score')}
                      labels={item.competency_labels}
                      points={item.competency_min_scores}
                      comparePoints={compared || cultureCompared}
                      compareTitle="candidate"
                      descriptions={item.competencies.map((el: any) => ({
                        description: el.competency.description,
                        name: el.competency.name,
                        id: el.competency.id
                      }))}
                      candidateCompetencies={competenciesForComparison || []}
                      scoresTooltipIsOpen={scoresTooltipIsOpen}
                      setScoresTooltipIsOpen={setScoresTooltipIsOpen}
                      showCompetencyComparison={showCompetencyComparison}
                      style={{ minHeight: '393px' }}
                    />
                  );
                }

                // Only mindgage metrics
                if (hasMindgageMetrics && mindgageChartData) {
                  return (
                    <CultureProfileCharts
                      title={t('Cognitive Metrics')}
                      isMindgage={true}
                      labels={mindgageChartData.labels}
                      points={mindgageChartData.points}
                      comparePoints={
                        mindgageChartData.comparePoints.some(
                          (p: any) => p !== null && p !== undefined
                        )
                          ? mindgageChartData.comparePoints
                          : undefined
                      }
                      compareTitle="candidate"
                      descriptions={mindgageChartData.descriptions}
                      profile={true}
                      showTooltips={false}
                      minScale={1}
                      maxScale={5}
                      mindgageScoresTooltipIsOpen={mindgageScoresTooltipIsOpen}
                      setMindgageScoresTooltipIsOpen={
                        setMindgageScoresTooltipIsOpen
                      }
                      showCompetencyComparison={mindgageChartData.comparePoints.some(
                        (p: any) => p !== null && p !== undefined
                      )}
                    />
                  );
                }

                // Neither exists - show placeholder with blurred chart and buttons
                return (
                  <Box sx={{ position: 'relative', minHeight: '400px' }}>
                    <Box
                      sx={{
                        filter: 'blur(4px)',
                        pointerEvents: 'none',
                        opacity: 0.5,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '400px',
                        width: '100%'
                      }}
                    >
                      <Tabs
                        onClick={(e) => e.stopPropagation()}
                        value={activeTab}
                        onChange={(e, newValue) => setActiveTab(newValue)}
                        indicatorColor="primary"
                        textColor="primary"
                      >
                        {!hasCompetencies && !hasMindgageMetrics && (
                          <Tab label={t('Competencies')} />
                        )}
                      </Tabs>
                      <CultureProfileCharts
                        title={t('Competencies Score')}
                        labels={placeholderChartData.labels}
                        points={placeholderChartData.points}
                        descriptions={placeholderChartData.descriptions}
                        showCompetencyComparison={false}
                        style={{ minHeight: '393px' }}
                      />
                    </Box>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px',
                        alignItems: 'center',
                        zIndex: 1
                      }}
                    >
                      {(isAdmin || isRecruiterPlus || isRecruiter) &&
                        (!company?.if_no_test || company?.if_mindgage_only) && (
                          <ButtonPrimary
                            onClick={() =>
                              navigate(
                                PATH_DASHBOARD.jobPosts.getJobPostUrl(item.id)
                              )
                            }
                            sx={{
                              minWidth: '150px'
                            }}
                          >
                            {t('Edit job')}
                          </ButtonPrimary>
                        )}
                      {isAdmin &&
                        company?.if_no_test &&
                        !company?.if_mindgage_only && (
                          <ButtonPrimary
                            onClick={() =>
                              navigate(PATH_DASHBOARD.company.cmsSetting)
                            }
                            sx={{
                              minWidth: '150px'
                            }}
                          >
                            {t('Enable Tests')}
                          </ButtonPrimary>
                        )}
                    </Box>
                  </Box>
                );
              })()}
            </Grid>
          </Grid>
          {editable && (
            <CardFooterContainer canTakeAction={canTakeAction}>
              {canTakeAction && (
                <Box>
                  <EditButton onClick={onEdit} />
                  <DeleteButton onClick={onDelete} />
                </Box>
              )}
              <SharedButtons
                url={`${HOST_URL}${PATH_HOME.getJobUrl(item.slug)}`}
                title={item.title}
                description={item.description}
              />
            </CardFooterContainer>
          )}
          <Grid
            style={{
              minHeight: editable ? '0' : '36px',
              marginTop: '8px',
              marginBottom: '16px'
            }}
          >
            {!editable && item.assessment_required && !competencies && (
              <TestAssessmentButton style={{ width: '100%' }} />
            )}
            {isCandidateOrEmployee &&
              (!item.assessment_required || competencies) && (
                <ApplyJobButton
                  jobPostId={item.id}
                  onApply={() => navigate(0)}
                  isApplied={!!item?.is_applied}
                  style={{ width: '100%' }}
                />
              )}
          </Grid>
        </CardContent>
      </CardStyles>
      {showModal && !isCandidateOrEmployee && !scoresTooltipIsOpen && (
        <CandidateModal
          open={showModal}
          handleClose={onCloseModal}
          candidate={mainInfo}
          profile={getCompetencyScoresAndLabels(item?.competencies)}
          cultureProfile={getCompetencyScoresAndLabels(
            item?.culture_profile?.competencies
          )}
          item={item}
          showCompetencyComparison={showCompetencyComparison}
        />
      )}
      <ChartDescription
        labels={(() => {
          // If both charts exist and tabs are shown, use active tab to determine data
          if (hasCompetencies && hasMindgageMetrics) {
            return activeTab === 0 ? sortedPoints : sortedMindgagePoints;
          }
          // If only mindgage exists, use mindgage data
          if (hasMindgageMetrics && !hasCompetencies) {
            return sortedMindgagePoints;
          }
          // Otherwise, use competencies data
          return sortedPoints;
        })()}
        candidateCompetencies={
          (hasCompetencies && hasMindgageMetrics && activeTab === 1) ||
          (hasMindgageMetrics && !hasCompetencies)
            ? [] // Mindgage doesn't use candidateCompetencies in the same way
            : competenciesForComparison || []
        }
        showCompetencyComparison={
          (hasCompetencies && hasMindgageMetrics && activeTab === 1) ||
          (hasMindgageMetrics && !hasCompetencies)
            ? mindgageChartData?.comparePoints?.some(
                (p: any) => p !== null && p !== undefined
              ) || false
            : showCompetencyComparison
        }
        jobPostCompetencies={(() => {
          // If both charts exist, tabs are shown, and mindgage tab is active, use mindgage descriptions
          if (hasCompetencies && hasMindgageMetrics && activeTab === 1) {
            return mindgageChartData?.descriptions || [];
          }
          // If only mindgage exists, use mindgage descriptions
          if (hasMindgageMetrics && !hasCompetencies) {
            return mindgageChartData?.descriptions || [];
          }
          // Otherwise, use competencies
          return item.competencies;
        })()}
      />
    </>
  );
};

export const SimpleJobCard = ({
  item,
  short = true,
  color
}: {
  item: any;
  short?: boolean;
  color: string;
}): JSX.Element => {
  const { t } = useLocales();
  const { logo }: any = useSelector(
    (state: RootState) => state.generalRecruiter.company
  );
  const company: any = useSelector(
    (state: RootState) => state.generalRecruiter.company
  );
  // const lng = localStorage.getItem('i18nextLng');
  const opt: any = useSelector((state: RootState) => state.fixturesReducer);
  const options = { ...opt };
  if (company.currency === 'INR') {
    options.salaries = SALARY_CHOICES_INR;
  }
  const fields = getCardValuesByType('job-post-questions', item, options);

  return (
    <Card
      style={{
        padding: '35px 50px',
        width: '100%',
        background: '#FFFFFF',
        boxShadow: '0px 0px 12px rgba(0, 0, 0, 0.08)',
        borderRadius: '25px',
        ...(!short && {
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between'
        })
      }}
    >
      <GridStyle>
        <BoxStyle style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Logo
            url={logo}
            sx={{
              height: '60px',
              p: 0
            }}
          />
          <TitlePrimary1 style={{ fontSize: '40px', color }}>
            {item.salary
              ? `${getNameByVal(options.salaries, item.salary)} ${
                  company.currency
                }`
              : `${getNameByVal(options.hourly_rates, item.hourly_rate)} ${
                  company.currency
                }${t('/Hour')}`}
          </TitlePrimary1>
        </BoxStyle>
        <Box>
          {!short ? (
            <Title6 className="title" style={{ margin: '20px 0', color }}>
              {item.title}
            </Title6>
          ) : (
            <Title5 className="title" style={{ margin: '20px 0', color }}>
              {item.title}
            </Title5>
          )}
          <Description5
            style={{
              ...(!short && {
                paddingBottom: '35px'
              })
            }}
            dangerouslySetInnerHTML={{
              __html: item.description
            }}
          />
        </Box>
        {short ||
          fields.map((el: any, index: number) => (
            <Box key={index}>
              <Title6
                className="title"
                sx={{ margin: '20px 0' }}
                style={{ color }}
              >
                {el.label}
              </Title6>
              <Description5
                sx={{
                  pb: '35px'
                }}
              >
                {el.value}
              </Description5>
            </Box>
          ))}
      </GridStyle>
      {!short && (
        <ButtonPrimary
          style={{
            maxWidth: 'max-content',
            background: `linear-gradient(92.25deg, ${color}90 -2.57%, ${color} 113.38%)`
          }}
          component={RouterLink}
          to={PATH_AUTH.register}
        >
          {t('apply for this job')}
        </ButtonPrimary>
      )}
    </Card>
  );
};
